import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarDays, Clock3, Hourglass, IndianRupee } from "lucide-react";
import { MaskedLine, Eyebrow } from "./Motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1643682661044-f0c34205dd65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHx3b21lbiUyMG1lZGl0YXRpb24lMjBicmVhdGh3b3JrJTIwY2lyY2xlJTIwd2FybSUyMHRvbmVzfGVufDB8fHx8MTc5MTgxNjQ5NHww&ixlib=rb-4.1.0&q=85";

const Geometry = ({ className = "", float = 0 }) => (
  <motion.svg
    viewBox="0 0 200 200"
    fill="none"
    className={className}
    animate={{ y: [0, -14, 0], rotate: [0, 5, 0] }}
    transition={{ duration: 9 + float, repeat: Infinity, ease: "easeInOut" }}
  >
    <circle cx="100" cy="100" r="86" stroke="#D9C3A3" strokeWidth="1" />
    <circle cx="100" cy="100" r="62" stroke="#E3C2BC" strokeWidth="1" />
    <circle cx="100" cy="100" r="38" stroke="#5E7D6A" strokeWidth="1" strokeDasharray="3 6" />
    <circle cx="100" cy="14" r="3.5" fill="#5E7D6A" />
    <circle cx="186" cy="100" r="3.5" fill="#E3C2BC" />
    <circle cx="100" cy="186" r="3.5" fill="#D9C3A3" />
    <circle cx="14" cy="100" r="3.5" fill="#5E7D6A" />
  </motion.svg>
);

const Pill = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-[#D9C3A3]/70 bg-white/60 px-4 py-2 text-xs sm:text-sm text-[#262320] backdrop-blur">
    <Icon size={14} className="text-[#5E7D6A]" />
    {children}
  </span>
);

export const Hero = ({ config, onReserve }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const geoY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="hero-section">
      <motion.div style={{ y: geoY }} className="pointer-events-none absolute inset-0">
        <Geometry className="absolute -left-24 top-24 h-72 w-72 opacity-60" float={0} />
        <Geometry className="absolute right-[8%] top-10 h-44 w-44 opacity-50" float={2} />
        <Geometry className="absolute bottom-0 left-[38%] h-56 w-56 opacity-40" float={4} />
      </motion.div>
      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-[#E8E3F0] blur-3xl opacity-60" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-8%] h-[420px] w-[420px] rounded-full bg-[#F4E3E0] blur-3xl opacity-70" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:px-12">
        <div>
          <MaskedLine delay={0.05}>
            <Eyebrow>An online wellness circle for women</Eyebrow>
          </MaskedLine>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#262320] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>{config?.workshop_title || "Soma & Soul"}</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="italic text-[#5E7D6A]">{config?.workshop_subtitle || "A Women's Wellness Circle"}</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.45} className="mt-7 max-w-xl">
            <p className="text-base leading-relaxed text-[#6E6862] sm:text-lg">
              {config?.tagline ||
                "An online morning of guided breathwork, gentle somatic movement, expressive art and reflective journaling."}
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap gap-3"
            data-testid="hero-workshop-pills"
          >
            <Pill icon={CalendarDays}>{config?.date || "Date to be announced"}</Pill>
            <Pill icon={Clock3}>{config?.time || "Time to be announced"}</Pill>
            <Pill icon={Hourglass}>{config?.duration || "Duration TBA"}</Pill>
            <Pill icon={IndianRupee}>{config?.price_inr ? `₹${config.price_inr.toLocaleString("en-IN")}` : "Price TBA"}</Pill>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-reserve-button"
              onClick={onReserve}
              className="group inline-flex items-center gap-3 rounded-full bg-[#5E7D6A] px-8 py-4 text-sm font-medium tracking-wide text-[#FAF7F2] shadow-lg shadow-[#5E7D6A]/25 transition-[background-color,transform] duration-300 hover:bg-[#4d6a59] hover:-translate-y-0.5"
            >
              Reserve Your Spot
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="text-xs text-[#6E6862]">Limited seats · confirmation email with Zoom link after payment</p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-sm lg:max-w-none"
        >
          <div className="absolute -inset-6 rounded-t-full bg-[#E8E3F0]/70" />
          <motion.div style={{ y: imgY }} className="relative">
            <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[28px] border border-[#D9C3A3]/60 shadow-2xl shadow-[#5E7D6A]/15">
              <img
                src={HERO_IMG}
                alt="Women sitting in a calm breathwork circle in warm light"
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#262320]/25 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 rounded-2xl border border-[#D9C3A3]/60 bg-white/85 px-5 py-4 backdrop-blur-md shadow-xl shadow-[#262320]/10">
              <p className="font-serif text-lg text-[#262320]">Breathe · Move · Create · Reflect</p>
              <p className="mt-1 text-xs text-[#6E6862]">One gentle morning, held online</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
